import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import useToast from "../../../hooks/useToast";
import useAuthContext from "../../../hooks/useAuthContext";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    fetchAuthorizeNetKeys,
    fetchUserDetails,
    fetchUserPractices,
} from "../services/auth.services";
import {
    IAuthorizeNetCredentials,
    IClientPractice,
    IUser,
} from "../types/auth.types";
import { isIncompletePatientPortalAccountError } from "../helpers/auth.helpers";
import { USER_DETAILS_QUERY_KEY } from "../constants/auth.constants";
import { isOtpRequired, markOtpRequired } from "../../../utils/otpSessionStore";

let hasHandledUserDetailsFailure = false;

export function useFetchUserDetails({
    enabled = true,
}: { enabled?: boolean } = {}) {
    const { toast } = useToast();
    const { user, logOutUser } = useAuthContext();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const shouldSkipUserDetailsCall = [
        "/login",
        "/otp-verification",
        "/incomplete-account",
    ].includes(pathname);
    const requiresOtp = Boolean(user?.uid && isOtpRequired(user.uid));

    if (!user) {
        hasHandledUserDetailsFailure = false;
    }

    return useQuery<IApiResponse<IUser>, AxiosError<IApiResponse<IUser>>>({
        queryKey: USER_DETAILS_QUERY_KEY,
        queryFn: () => fetchUserDetails(),
        onSuccess: () => {
            hasHandledUserDetailsFailure = false;
        },
        onError: (error) => {
            if (error?.response?.status === 428) {
                if (user?.uid) {
                    markOtpRequired(user.uid);
                }

                Sentry.logger.warn("Patient portal OTP required", {
                    area: "auth-bootstrap",
                    route: pathname,
                    source: "user-details-query",
                });
                navigate("/otp-verification", { replace: true });
                return;
            }

            if (isIncompletePatientPortalAccountError(error)) {
                navigate(`/incomplete-account`, { replace: true });
                return;
            }

            if (hasHandledUserDetailsFailure) {
                return;
            }

            hasHandledUserDetailsFailure = true;
            toast({
                mode: "error",
                message:
                    error?.response?.data.message ||
                    error?.message ||
                    "Could not log user in at this time",
            });
            logOutUser().catch(() => undefined);
        },
        enabled:
            Boolean(user) &&
            !requiresOtp &&
            !shouldSkipUserDetailsCall &&
            enabled,
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
}

export function useFetchUserPractices(userId: string) {
    const { toast } = useToast();
    const navigate = useNavigate();

    return useQuery<
        IApiResponse<IClientPractice>,
        AxiosError<IApiResponse<IClientPractice>>
    >({
        queryKey: ["user-practices", userId],
        queryFn: () => fetchUserPractices(userId),
        enabled: Boolean(userId),
        onError: (error) => {
            if (isIncompletePatientPortalAccountError(error)) {
                navigate(`/incomplete-account`, { replace: true });
                return;
            }

            toast({
                mode: "error",
                message:
                    error?.response?.data.message ||
                    "Could not load practices at this time",
            });
        },
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
}

export function useFetchAuthorizeNetCredentials(enabled = true) {
    return useQuery<
        IApiResponse<IAuthorizeNetCredentials>,
        AxiosError<IApiResponse<IAuthorizeNetCredentials>>
    >({
        queryKey: ["authorize-net-credentials"],
        queryFn: () => fetchAuthorizeNetKeys(),
        enabled,
    });
}
